import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { Alert, Box, Card, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { FieldRow } from '~/components/common/detailFields';
import { HeaderField } from '~/components/common/HeaderField';
import { SectionTitle } from '~/components/common/SectionTitle';
import type { ContractReview } from '~/components/contractReview/types';
import MonthRangePicker from '~/components/date/MonthRangePicker/MonthRangePicker';
import type { SelectedDateRange } from '~/components/date/MonthRangePicker/types';
import { useGlobalFilterContext } from '~/contexts/globalFilterContext';
import { useContractKpis } from '~/data/hooks/useContractKpis';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import type { ResolvedTarget, TargetDirection } from '~/lib/kpi/contractTargets';
import {
  formatTargetValues,
  FULL_MAINTENANCE_COMPLETION,
  meetsTarget,
  resolveCarRateTarget,
  resolveOnTimeTarget,
} from '~/lib/kpi/contractTargets';
import { formatDecimal, formatPercent } from '~/utils/format';

/** Where the target came from, said plainly rather than left to be guessed. */
const targetCaption = (
  target: ResolvedTarget | null,
  direction: TargetDirection,
  format: (value: number) => string,
): string => {
  if (target === null) return 'No target set';
  const range = formatTargetValues(target, format);
  const written = direction === 'atMost' ? `under ${range}` : `over ${range}`;
  return target.source === 'cars' ? `Target ${written}, per elevator` : `Target ${written}`;
};

function KpiField({
  label,
  loaded,
  value,
  isGood,
  caption,
}: {
  label: string;
  loaded: boolean;
  value: string | null;
  /** `undefined` draws no mark — see `meetsTarget`. */
  isGood?: boolean;
  caption: string;
}) {
  return (
    <HeaderField label={label} caption={caption}>
      {!loaded ? (
        <Skeleton width={70} animation="wave" />
      ) : value === null ? (
        <Typography variant="body2">—</Typography>
      ) : (
        <Box display="flex" alignItems="center" gap={0.5}>
          {isGood === undefined ? null : isGood ? (
            <CheckCircleIcon sx={{ fontSize: 16 }} color="success" />
          ) : (
            <WarningIcon sx={{ fontSize: 16, color: '#ED6C02' }} />
          )}
          <Typography variant="body2">{value}</Typography>
        </Box>
      )}
    </HeaderField>
  );
}

/**
 * How the contract is being honoured, over a period — the other half of the KPIs tab,
 * which states what the contract asks for and stops there.
 *
 * Judged against the contract's *own* targets: the per-elevator rates on
 * `PropertyContractCar` first, the contract-wide columns second, and no mark at all
 * where neither is set. The app's other performance strips compare every contract to
 * the same four constants, so a contract whose elevators are set to 2 callbacks a year
 * still reads as meeting its target at 3.5.
 *
 * Contract-wide figures, not per property: `byContract` aggregates over the scope
 * requested, and a per-property split would be one request per building.
 */
export function ContractPerformanceTab({ contract }: { contract: ContractReview }) {
  const terms = useLocaleTerms();

  // Opens on whatever the rest of the app is scoped to, so the figures match the
  // list row this contract was most likely opened from.
  const {
    selectedFilters: { date },
  } = useGlobalFilterContext();
  const [period, setPeriod] = useState<{ start: Date; end: Date }>({
    start: date.start,
    end: date.end,
  });

  const propertyIds = useMemo(
    () => contract.properties.map((property) => property.id),
    [contract.properties],
  );
  const cars = useMemo(
    () => contract.properties.flatMap((property) => property.cars),
    [contract.properties],
  );

  const { kpis, isLoading } = useContractKpis(contract.id, propertyIds, { dateRange: period });

  const callbackTarget = useMemo(
    () =>
      resolveCarRateTarget(
        cars,
        (car) => car.regularCallbackRate,
        contract.kpiTargets.callbackRateTarget,
      ),
    [cars, contract.kpiTargets.callbackRateTarget],
  );
  const emergencyTarget = useMemo(
    () =>
      resolveCarRateTarget(
        cars,
        (car) => car.emergencyCallbackRate,
        contract.kpiTargets.emergencyCallRateTarget,
      ),
    [cars, contract.kpiTargets.emergencyCallRateTarget],
  );
  const onTimeTarget = useMemo((): ResolvedTarget | null => {
    const fraction = resolveOnTimeTarget(contract.kpiTargets.onTimeResponseTargetPct);
    return fraction === null ? null : { value: fraction, values: [fraction], source: 'contract' };
  }, [contract.kpiTargets.onTimeResponseTargetPct]);

  // Not a guessed threshold: `maintenance_completion` is already normalised against each
  // elevator's own contracted quantity, so the whole of it is the target by construction.
  const completionTarget: ResolvedTarget = {
    value: FULL_MAINTENANCE_COMPLETION,
    values: [FULL_MAINTENANCE_COMPLETION],
    source: 'contract',
  };

  // The picker reports a cleared range while a selection is half-made; the KPIs
  // have to be of *some* period, so hold the last complete one.
  const onPeriodChange = (next: SelectedDateRange) => {
    if (next.start && next.end) {
      setPeriod({ start: next.start, end: next.end });
    }
  };

  const loaded = !isLoading;
  const num = (value: number | null | undefined) =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

  const completion = num(kpis?.maintenanceCompletion);
  const onTime = num(kpis?.onTimeResponse);
  const callbackRate = num(kpis?.callbackRate);
  const emergencyRate = num(kpis?.emergencyCallbackRate);

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Stack gap={3}>
        <Card variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
            <SectionTitle>Performance</SectionTitle>
            <MonthRangePicker placeHolder="Period" value={period} onSelect={onPeriodChange} />
          </Stack>

          {propertyIds.length === 0 ? (
            <Box mt={2}>
              <Alert severity="info">
                This contract covers no property, so there is nothing to measure against it.
              </Alert>
            </Box>
          ) : (
            <>
              <Box mt={2}>
                <FieldRow>
                  <KpiField
                    label="Maintenance completion"
                    loaded={loaded}
                    value={completion === null ? null : formatPercent(completion)}
                    isGood={meetsTarget(completion, completionTarget, 'atLeast')}
                    caption="Target 100%"
                  />
                  <KpiField
                    label="On-time response"
                    loaded={loaded}
                    value={onTime === null ? null : formatPercent(onTime)}
                    isGood={meetsTarget(onTime, onTimeTarget, 'atLeast')}
                    caption={targetCaption(onTimeTarget, 'atLeast', formatPercent)}
                  />
                  <KpiField
                    label={terms.callbackRate}
                    loaded={loaded}
                    value={callbackRate === null ? null : formatDecimal(callbackRate)}
                    isGood={meetsTarget(callbackRate, callbackTarget, 'atMost')}
                    caption={targetCaption(callbackTarget, 'atMost', formatDecimal)}
                  />
                  <KpiField
                    label={terms.emergencyCallbackRate}
                    loaded={loaded}
                    value={emergencyRate === null ? null : formatDecimal(emergencyRate)}
                    isGood={meetsTarget(emergencyRate, emergencyTarget, 'atMost')}
                    caption={targetCaption(emergencyTarget, 'atMost', formatDecimal)}
                  />
                </FieldRow>
              </Box>

              <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                Across{' '}
                {contract.properties.length === 1
                  ? contract.properties[0].name
                  : `all ${contract.properties.length} properties on this contract`}
                .{' '}
                <Tooltip title="Targets set on the contract's elevators are used where they exist, and the contract's own where they do not.">
                  <Box component="span" sx={{ textDecoration: 'underline dotted', cursor: 'help' }}>
                    Where do the targets come from?
                  </Box>
                </Tooltip>
              </Typography>
            </>
          )}
        </Card>
      </Stack>
    </Box>
  );
}
